import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { useNavigate } from 'react-router-dom';
import './tailwind.css';
import { FaEdit, FaTrash, FaKey, FaCheck, FaTimes, FaSpinner } from 'react-icons/fa'; // Icone

export default function GestisciAgentiAgenzia() {
  const [agentiList, setAgentiList] = useState([]);
  const [agenzieOptions, setAgenzieOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [userAgencyId, setUserAgencyId] = useState(null); // ID dell'agenzia dell'utente loggato
  const [userAgencyName, setUserAgencyName] = useState(''); // Nome dell'agenzia dell'utente loggato
  const navigate = useNavigate();

  // Stato per la modale di conferma/input password
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  // Modificato: onConfirmAction ora sarà null se non c'è una callback da eseguire
  const [onConfirmAction, setOnConfirmAction] = useState(null);
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const SUPABASE_FUNCTIONS_BASE_URL = 'https://zwgpmobugscpdcavajbu.supabase.co'; // Assicurati che sia l'URL corretto

  // Stato per la modifica in linea
  const [editingId, setEditingId] = useState(null);
  const [editedData, setEditedData] = useState({});

  // Funzioni per la modale di conferma personalizzata
  const openConfirmModal = useCallback((title, message, confirmCallback, showPassword = false) => {
    setModalTitle(title);
    setModalMessage(message);
    // Imposta direttamente la callback. Se confirmCallback è null, onConfirmAction sarà null.
    setOnConfirmAction(() => confirmCallback);
    setShowPasswordInput(showPassword);
    setTempPassword('');
    setShowConfirmModal(true);
  }, []);

  const closeConfirmModal = useCallback(() => {
    setShowConfirmModal(false);
    setModalTitle('');
    setModalMessage('');
    // Cruciale: resetta questo a null quando chiudi, per garantire "OK" al prossimo openConfirmModal senza callback
    setOnConfirmAction(null);
    setShowPasswordInput(false);
    setTempPassword('');
  }, []);

  const handleConfirm = () => {
    if (onConfirmAction) {
      // Passa il valore corrente dello stato tempPassword alla funzione di azione
      // SOLO SE l'input password è stato mostrato nella modale
      if (showPasswordInput) {
        onConfirmAction(tempPassword);
      } else {
        onConfirmAction();
      }
    }
  };

  // Funzione per caricare gli agenti e le relative agenzie
  const loadAgenti = useCallback(async (agencyIdToFilter = null) => {
    setLoading(true);
    try {
      console.log('Caricamento utenti con ruolo "account" e relative sedi...');

      // 1. Carica tutti gli utenti con ruolo 'agente'
      let query = supabase
        .from('utenti')
        .select('id, nome, cognome, email, agenzia_id')
        .eq('ruolo', 'agente');

      // Se è presente un agencyIdToFilter, filtra per quello
      if (agencyIdToFilter) {
        query = query.eq('agenzia_id', agencyIdToFilter);
      }

      const { data: usersData, error: usersError } = await query;

      if (usersError) throw usersError;

      // 2. Carica tutte le agenzie (per il dropdown e per i nomi)
      let agenzieData;
      if (agencyIdToFilter) {
        // Se è un'agenzia, carica solo la sua agenzia
        const { data, error } = await supabase
          .from('agenzie')
          .select('id, nome')
          .eq('id', agencyIdToFilter);
        if (error) throw error;
        agenzieData = data;
      } else {
        // Se è un broker, carica tutte le agenzie
        const { data, error } = await supabase
          .from('agenzie')
          .select('id, nome');
        if (error) throw error;
        agenzieData = data;
      }

      const agenzieMap = agenzieData.reduce((acc, agency) => {
        acc[agency.id] = agency;
        return acc;
      }, {});

      setAgenzieOptions(agenzieData);

      // 3. Combina i dati
      const combinedData = usersData.map(user => ({
        userId: user.id,
        nomeUtente: user.nome,
        cognomeUtente: user.cognome,
        emailUtente: user.email,
        agenziaId: user.agenzia_id,
        nomeAgenzia: agenzieMap[user.agenzia_id]?.nome || 'N/D',
      }));

      setAgentiList(combinedData);
      console.log('Agenti + agenzie:', combinedData);

    } catch (error) {
      console.error('Errore nel caricamento:', error.message);
      openConfirmModal('Errore', `Errore nel caricamento Account: ${error.message}`, null);
      setAgentiList([]);
    } finally {
      setLoading(false);
    }
  }, [openConfirmModal]);

  useEffect(() => {
    const fetchUserRoleAndAgency = async () => {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;

      if (sessionError || !userId) {
        console.error('No user session or error:', sessionError?.message);
        navigate('/');
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('utenti')
        .select('ruolo, agenzia_id') // Seleziona anche agenzia_id
        .eq('id', userId)
        .single();

      if (profileError || !profile) {
        console.error('Error loading user profile:', profileError?.message);
        navigate('/');
        return;
      }

      // Reindirizzamento basato sul ruolo
      if (profile.ruolo !== 'broker' && profile.ruolo !== 'agenzia') {
        console.warn(`User with role ${profile.ruolo} attempted to access GestisciAgenti.`);
        navigate('/dashboard'); // Reindirizza a una dashboard generica o alla home
        return;
      }

      setUserRole(profile.ruolo);
      setUserAgencyId(profile.agenzia_id); // Imposta l'ID dell'agenzia per l'utente loggato

      if (profile.ruolo === 'agenzia' && profile.agenzia_id) {
        const { data: agencyData, error: agencyError } = await supabase
          .from('agenzie')
          .select('nome')
          .eq('id', profile.agenzia_id)
          .single();
        if (agencyError) {
          console.error('Error fetching agency name:', agencyError.message);
        } else {
          setUserAgencyName(agencyData?.nome || '');
        }
      }

      // Carica gli agenti in base al ruolo
      if (profile.ruolo === 'agenzia') {
        if (!profile.agenzia_id) {
          console.error('L\'utente agenzia non ha un agenzia_id associato.');
          openConfirmModal('Errore', 'La tua agenzia non è associata correttamente. Contatta l\'amministratore.', null);
          setLoading(false);
          return;
        }
        loadAgenti(profile.agenzia_id); // Carica solo gli agenti della propria agenzia
      } else { // broker
        loadAgenti(); // Carica tutti gli agenti
      }
    };

    fetchUserRoleAndAgency();

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        navigate('/');
      }
    });

    return () => {
      authListener?.data?.unsubscribe();
    };
  }, [navigate, loadAgenti, openConfirmModal]); // Aggiungi openConfirmModal alle dipendenze

  const handleEdit = (userData) => {
    setEditingId(userData.userId);
    setEditedData({
      userId: userData.userId,
      nomeUtente: userData.nomeUtente,
      cognomeUtente: userData.cognomeUtente,
      emailUtente: userData.emailUtente,
      agenziaId: userData.agenziaId,
      nomeAgenzia: userData.nomeAgenzia,
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditedData({});
  };

  const handleChangeEdit = (e) => {
    const { name, value } = e.target;
    setEditedData({ ...editedData, [name]: value });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      if (!editedData.nomeUtente || !editedData.cognomeUtente || !editedData.emailUtente || !editedData.agenziaId) {
        openConfirmModal('Errore di Validazione', 'Tutti i campi sono obbligatori.', null);
        setLoading(false);
        return;
      }

      // **NUOVA LOGICA: Chiama la tua Edge Function per l'aggiornamento**
      // Recupera la sessione per il token di autenticazione
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData?.session?.access_token) {
        throw new Error('No active session found. Please log in again.');
      }
      const accessToken = sessionData.session.access_token;

      // Prepara i dati da inviare alla Edge Function
      const payload = {
        userId: editedData.userId,
        nomeUtente: editedData.nomeUtente,
        cognomeUtente: editedData.cognomeUtente,
        emailUtente: editedData.emailUtente,
        agenziaId: editedData.agenziaId,
      };

      // Controllo aggiuntivo lato client per l'utente 'agenzia'
      // Questa è una buona pratica per dare feedback immediato,
      // ma la vera sicurezza è garantita dalla logica della Edge Function.
      if (userRole === 'agenzia' && payload.agenziaId !== userAgencyId) {
          openConfirmModal('Permesso Negato', 'Non puoi associare Account a Sedi diverse dalla tua.', null);
          setLoading(false);
          return;
      }


      const response = await fetch(`${SUPABASE_FUNCTIONS_BASE_URL}/functions/v1/update-user-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}` // Invia il token del broker/agenzia
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        // Se la risposta non è OK (status 4xx o 5xx), c'è stato un errore dalla funzione
        throw new Error(data.error || 'Errore durante l\'aggiornamento del profilo Account.');
      }

      // Logica di successo
      setEditingId(null);
      setEditedData({});
      // Ricarica la lista degli agenti in base al ruolo dell'utente
      await loadAgenti(userRole === 'agenzia' ? userAgencyId : null);

      openConfirmModal('Successo!', 'Account aggiornato con successo!', null);

    } catch (error) {
      console.error('Errore nel salvataggio:', error.message);
      openConfirmModal('Errore', `Si è verificato un errore nel salvataggio: ${error.message}.`, null);
    } finally {
      setLoading(false);
    }
  };

 const handleDeleteAgent = async (userId, userEmail, agentAgencyId) => {
    // Controllo che l'utente 'agenzia' possa eliminare solo i propri agenti
    if (userRole === 'agenzia' && agentAgencyId !== userAgencyId) {
      openConfirmModal('Permesso Negato', 'Non puoi eliminare Account di altre Sedi.', null);
      return;
    }

    openConfirmModal(
      'Conferma Eliminazione',
      `Sei sicuro di voler eliminare l'Account con email: "${userEmail}"? Questa azione è irreversibile.`,
      async () => {
        setLoading(true);
        try {
          const { data: sessionData } = await supabase.auth.getSession();
          const adminUserId = sessionData?.session?.user?.id || null;

          if (!adminUserId) {
            throw new Error('Impossibile recuperare l\'ID dell\'amministratore loggato.');
          }

          const response = await fetch(`${SUPABASE_FUNCTIONS_BASE_URL}/functions/v1/delete-user`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${sessionData?.session?.access_token || 'anon'}`
            },
            body: JSON.stringify({ userIdToDelete: userId, adminUserId: adminUserId }),
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || 'Errore durante l\'eliminazione dell\'Account.');
          }

          // *** MODIFICA QUI ***
          openConfirmModal('Successo!', `Account "${userEmail}" eliminato con successo!`, null); // Passa null per la callback
          await loadAgenti(userRole === 'agenzia' ? userAgencyId : null); // Ricarica la lista immediatamente
          // *** FINE MODIFICA ***

        } catch (error) {
          console.error('Errore durante l\'eliminazione dell\'Account:', error.message);
          openConfirmModal('Errore', `Errore nell'eliminazione: ${error.message}`, null);
        } finally {
          setLoading(false);
        }
      }
    );
  };

  const handleForcePasswordReset = (userId, userEmail, agentAgencyId) => {
    // Controllo che l'utente 'agenzia' possa resettare la password solo dei propri agenti
    if (userRole === 'agenzia' && agentAgencyId !== userAgencyId) {
      openConfirmModal('Permesso Negato', 'Non puoi resettare la password di Account di altre Sedi.', null);
      return;
    }

    openConfirmModal(
      'Forza Nuova Password',
      `Inserisci la nuova password per l'Account ${userEmail}.`,
      async (passwordFromModal) => { // La password aggiornata viene passata qui
        setLoading(true);
        try {
          const passwordToUse = passwordFromModal.trim();

          if (!passwordToUse || passwordToUse.length < 6) {
            openConfirmModal('Errore', 'La password deve essere di almeno 6 caratteri.', null);
            setLoading(false);
            return;
          }

          const { data: sessionData } = await supabase.auth.getSession();
          const accessToken = sessionData?.session?.access_token || 'anon';

          const response = await fetch(`${SUPABASE_FUNCTIONS_BASE_URL}/functions/v1/reset-user-password`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({ targetUserId: userId, newPassword: passwordToUse }),
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || 'Errore durante il reset della password.');
          }

          // Successo: mostra solo "OK". La callback è null.
          openConfirmModal('Successo!', 'Password aggiornata correttamente.', null);
        } catch (error) {
          console.error('Errore durante il reset della password:', error.message);
          openConfirmModal('Errore', `Errore nel reset password: ${error.message}`, null);
        } finally {
          setLoading(false);
        }
      },
      true, // Questo abilita correttamente l'input della password nella modale
      userId // Passa l'ID utente target
    );
  };

  if (userRole === null || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 text-slate-800">
        <FaSpinner className="animate-spin text-4xl text-mioblu mr-3" />
        <p className="text-xl font-semibold">Caricamento Account...</p>
      </div>
    );
  }

  if (userRole !== 'broker' && userRole !== 'agenzia') {
    return null; // O un messaggio di accesso negato
  }

  return (
    <div className="p-4 md:p-8 bg-gray-50 min-h-screen">
      <h1 className="text-2xl md:text-3xl font-bold uppercase mb-4 text-center text-mioblu">
        {userRole === 'broker' ? 'Gestisci Account (Broker)' : `Gestione Account ${userAgencyName}`}
      </h1>

      <div className="bg-white p-6 rounded-lg shadow-md mb-8 overflow-x-auto">
        <table className="min-w-full table-fixed border-collapse">
          <thead>
            <tr className="bg-gray-100 text-mioblu uppercase text-sm leading-normal">
              <th className="py-3 px-6 text-left w-1/5">Nome</th>
              <th className="py-3 px-6 text-left w-1/5">Cognome</th>
              <th className="py-3 px-6 text-left w-1/5">Email</th>
              <th className="py-3 px-6 text-left w-1/5">Sede</th>
              <th className="py-3 px-6 text-center w-1/5">Azioni</th>
            </tr>
          </thead>
          <tbody className="text-gray-600 text-sm font-light">
            {agentiList.length > 0 ? (
              agentiList.map((item) => (
                <tr key={item.userId} className="border-b border-gray-200 hover:bg-gray-50">
                  {editingId === item.userId ? (
                    <>
                      {/* Cella per Nome Agente (modificabile) */}
                      <td className="py-3 px-2 text-left align-middle">
                        <input
                          type="text"
                          name="nomeUtente"
                          value={editedData.nomeUtente}
                          onChange={handleChangeEdit}
                          className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                        />
                      </td>
                      {/* Cella per Cognome Agente (modificabile) */}
                      <td className="py-3 px-2 text-left align-middle">
                        <input
                          type="text"
                          name="cognomeUtente"
                          value={editedData.cognomeUtente}
                          onChange={handleChangeEdit}
                          className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                        />
                      </td>
                      {/* Cella per Email Agente (modificabile) */}
                      <td className="py-3 px-2 text-left align-middle">
                        <input
                          type="email"
                          name="emailUtente"
                          value={editedData.emailUtente}
                          onChange={handleChangeEdit}
                          className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                        />
                      </td>
                      {/* Cella per Agenzia Associata (modificabile tramite dropdown SOLO PER BROKER) */}
                      <td className="py-3 px-2 text-left align-middle">
                        {userRole === 'broker' ? (
                          <select
                            name="agenziaId"
                            value={editedData.agenziaId || ''}
                            onChange={handleChangeEdit}
                            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                          >
                            <option value="">Seleziona Agenzia</option>
                            {agenzieOptions.map((agency) => (
                              <option key={agency.id} value={agency.id}>
                                {agency.nome}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="p-2 border border-gray-300 rounded block bg-gray-100 text-gray-700 cursor-not-allowed">
                            {item.nomeAgenzia || 'N/D'}
                          </span>
                        )}
                      </td>
                      {/* Cella Azioni (Salva/Annulla) */}
                      <td className="py-3 px-2 text-center align-middle">
                        <div className="flex justify-center items-center gap-2">
                          <button
                            className="w-9 h-9 flex items-center justify-center p-0 text-base bg-green-500 text-white rounded transition-colors duration-200 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500"
                            onClick={handleSave}
                            title="Salva"
                            disabled={loading}
                          >
                            <FaCheck />
                          </button>
                          <button
                            className="w-9 h-9 flex items-center justify-center p-0 text-base bg-gray-500 text-white rounded transition-colors duration-200 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                            onClick={handleCancel}
                            title="Annulla"
                            disabled={loading}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      {/* Visualizzazione normale */}
                      <td className="py-3 px-6 text-left align-middle font-semibold">{item.nomeUtente || '-'}</td>
                      <td className="px-6 py-3 text-left align-middle">{item.cognomeUtente || '-'}</td>
                      <td className="py-3 px-6 text-left align-middle">{item.emailUtente || '-'}</td>
                      <td className="py-3 px-6 text-left align-middle">{item.nomeAgenzia || 'N/D'}</td>
                      <td className="py-3 px-6 text-center align-middle">
                        <div className="flex justify-center items-center gap-2 min-h-12">
                          <button
                            className="w-9 h-9 flex items-center justify-center p-0 text-base bg-blue-500 text-white rounded transition-colors duration-200 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onClick={() => handleEdit(item)}
                            title="Modifica"
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="w-9 h-9 flex items-center justify-center p-0 text-base bg-red-500 text-white rounded transition-colors duration-200 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                            onClick={() => handleDeleteAgent(item.userId, item.emailUtente, item.agenziaId)}
                            title="Elimina"
                          >
                            <FaTrash />
                          </button>
                          {item.userId && (
                            <button
                              className="w-9 h-9 flex items-center justify-center p-0 text-base bg-purple-500 text-white rounded transition-colors duration-200 hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
                              onClick={() => handleForcePasswordReset(item.userId, item.emailUtente, item.agenziaId)} // Passa agenziaId
                              title="Forza Nuova Password"
                            >
                              <FaKey />
                            </button>
                          )}
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="py-4 text-center text-gray-500">Nessun Account trovato.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modale di Conferma/Input Password Personalizzata */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full text-center">
            <h3 className="text-xl font-bold mb-4 text-gray-800">{modalTitle}</h3>
            <p className="text-gray-700 mb-6">{modalMessage}</p>

            {showPasswordInput && (
              <div className="mb-6">
                <label htmlFor="tempPassword" className="sr-only">Nuova password</label>
                <input
                  type="password"
                  id="tempPassword"
                  value={tempPassword}
                  onChange={(e) => setTempPassword(e.target.value)}
                  placeholder="Nuova password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Minimo 6 caratteri.</p>
              </div>
            )}

            <div className="flex justify-center gap-4">
              {/* Pulsante "Sì" (o azione di conferma) - Mostrato solo se c'è una onConfirmAction */}
              {onConfirmAction && (
                <button
                  className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  onClick={handleConfirm}
                  disabled={loading || (showPasswordInput && tempPassword.length < 6)} /* Disabilita se loading o password troppo corta */
                >
                  {loading ? <FaSpinner className="animate-spin" /> : 'Sì'}
                </button>
              )}
              {/* Pulsante "No" o "OK" */}
              <button
                className="px-6 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors"
                // Se c'è una onConfirmAction, questo è il "No", altrimenti è l'"OK" che chiude il modale
                onClick={closeConfirmModal}
                disabled={loading}
              >
                {onConfirmAction ? 'No' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
